import { z } from 'zod'

export const submitSoundValues = [
  'none',
  'award',
  'goal',
  'festive-melody',
  'positive-notification',
  'trumpet-fanfare',
  'video-game-treasure',
  'item-acquired',
] as const

export const alarmSoundValues = [
  'classic',
  'bell',
  'chime',
  'pulse',
  'urgent',
  'arcade',
  'clock-alarm',
  'clock-bells',
  'cricket',
  'emergency',
  'kitchen-ding',
  'marimba',
  'marimba-waiting',
  'slot-machine',
  'vintage-phone',
  'vintage-warning',
  'bonus',
] as const

export const userSettingsSchema = z.object({
  editor: z.object({
    defaultLanguage: z.enum(['python', 'javascript', 'java', 'cpp', 'go']),
    font: z.enum(['default', 'fira-code']),
    fontSize: z.number().int().min(12).max(20),
    fontLigatures: z.boolean(),
    keyBinding: z.enum(['standard', 'vim']),
    tabSize: z.union([z.literal(2), z.literal(4)]),
    wordWrap: z.boolean(),
    relativeLineNumbers: z.boolean(),
    successSubmitSound: z.enum(submitSoundValues),
    suppressExpirationNotice: z.boolean().default(false),
  }),
  shortcuts: z.object({
    runCode: z.boolean(),
    submit: z.boolean(),
  }),
  postmortem: z.object({
    showDifficultyRating: z.boolean(),
  }),
  timer: z
    .object({
      alarmSound: z.enum(alarmSoundValues).default('kitchen-ding'),
      autoReset: z.boolean().default(true),
      superAlarmEnabled: z.boolean().default(false),
      superAlarmThresholds: z.array(z.number().positive()).max(3).default([]),
      recordOverage: z.boolean().default(true),
      resetTimerTo: z.enum(['lastDuration', 'zero']).default('lastDuration'),
      showSessionTransitionNotice: z.boolean().default(true),
    })
    .default({
      alarmSound: 'kitchen-ding',
      autoReset: true,
      superAlarmEnabled: false,
      superAlarmThresholds: [],
      recordOverage: true,
      resetTimerTo: 'lastDuration',
      showSessionTransitionNotice: true,
    }),
  layouts: z.object({
    activeLayoutId: z.string(),
    custom: z.array(
      z.object({
        id: z.string(),
        name: z.string(),
        model: z.record(z.string(), z.unknown()),
      }),
    ),
  }),
})

export type UserSettings = z.infer<typeof userSettingsSchema>

/** Partial (deep) version for PATCH-style updates */
export const partialUserSettingsSchema = z.object({
  editor: z
    .object({
      defaultLanguage: z
        .enum(['python', 'javascript', 'java', 'cpp', 'go'])
        .optional(),
      font: z.enum(['default', 'fira-code']).optional(),
      fontSize: z.number().int().min(12).max(20).optional(),
      fontLigatures: z.boolean().optional(),
      keyBinding: z.enum(['standard', 'vim']).optional(),
      tabSize: z.union([z.literal(2), z.literal(4)]).optional(),
      wordWrap: z.boolean().optional(),
      relativeLineNumbers: z.boolean().optional(),
      successSubmitSound: z.enum(submitSoundValues).optional(),
      suppressExpirationNotice: z.boolean().optional(),
    })
    .optional(),
  shortcuts: z
    .object({
      runCode: z.boolean().optional(),
      submit: z.boolean().optional(),
    })
    .optional(),
  postmortem: z
    .object({
      showDifficultyRating: z.boolean().optional(),
    })
    .optional(),
  timer: z
    .object({
      alarmSound: z.enum(alarmSoundValues).optional(),
      autoReset: z.boolean().optional(),
      superAlarmEnabled: z.boolean().optional(),
      superAlarmThresholds: z.array(z.number().positive()).max(3).optional(),
      recordOverage: z.boolean().optional(),
      resetTimerTo: z.enum(['lastDuration', 'zero']).optional(),
      showSessionTransitionNotice: z.boolean().optional(),
    })
    .optional(),
  layouts: z
    .object({
      activeLayoutId: z.string().optional(),
      custom: z
        .array(
          z.object({
            id: z.string(),
            name: z.string(),
            model: z.record(z.string(), z.unknown()),
          }),
        )
        .optional(),
    })
    .optional(),
})

export type PartialUserSettings = z.infer<typeof partialUserSettingsSchema>

export const DEFAULT_USER_SETTINGS: UserSettings = {
  editor: {
    defaultLanguage: 'python',
    font: 'default',
    fontSize: 14,
    fontLigatures: false,
    keyBinding: 'standard',
    tabSize: 2,
    wordWrap: false,
    relativeLineNumbers: false,
    successSubmitSound: 'none' as const,
    suppressExpirationNotice: false,
  },
  shortcuts: {
    runCode: true,
    submit: true,
  },
  postmortem: {
    showDifficultyRating: true,
  },
  timer: {
    alarmSound: 'kitchen-ding' as const,
    autoReset: true,
    superAlarmEnabled: false,
    superAlarmThresholds: [],
    recordOverage: true,
    resetTimerTo: 'lastDuration' as const,
    showSessionTransitionNotice: true,
  },
  layouts: {
    activeLayoutId: 'default',
    custom: [],
  },
}

/**
 * Deep-merge partial settings onto a base settings object.
 * Only overrides keys that are explicitly provided (not undefined).
 */
export function mergeSettings(
  base: UserSettings,
  partial: PartialUserSettings,
): UserSettings {
  return {
    editor: {
      ...base.editor,
      ...(partial.editor &&
        Object.fromEntries(
          Object.entries(partial.editor).filter(([, v]) => v !== undefined),
        )),
    },
    shortcuts: {
      ...base.shortcuts,
      ...(partial.shortcuts &&
        Object.fromEntries(
          Object.entries(partial.shortcuts).filter(([, v]) => v !== undefined),
        )),
    },
    postmortem: {
      ...base.postmortem,
      ...(partial.postmortem &&
        Object.fromEntries(
          Object.entries(partial.postmortem).filter(([, v]) => v !== undefined),
        )),
    },
    timer: {
      ...base.timer,
      ...(partial.timer &&
        Object.fromEntries(
          Object.entries(partial.timer).filter(([, v]) => v !== undefined),
        )),
    },
    layouts: {
      ...base.layouts,
      ...(partial.layouts &&
        Object.fromEntries(
          Object.entries(partial.layouts).filter(([, v]) => v !== undefined),
        )),
    },
  }
}

/**
 * Resolve stored settings (possibly null/partial from DB) into a complete UserSettings object.
 */
export function resolveSettings(stored: unknown): UserSettings {
  if (!stored || typeof stored !== 'object') {
    return DEFAULT_USER_SETTINGS
  }
  const parsed = partialUserSettingsSchema.safeParse(stored)
  if (!parsed.success) {
    return DEFAULT_USER_SETTINGS
  }
  return mergeSettings(DEFAULT_USER_SETTINGS, parsed.data)
}
